import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, CalendarDays, MapPin, Users, Clock, Sparkles } from "lucide-react";
import { PRICING, UPCOMING_CAMPS, CONTACT } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyles = {
  sky: {
    card: "bg-sky-400 text-slate-900 border-sky-300",
    tag: "bg-slate-900 text-white",
    check: "text-slate-900 bg-white/40",
    feat: "text-slate-800",
    btn: "bg-slate-900 text-white hover:bg-slate-800",
    note: "text-slate-800/70",
  },
  white: {
    card: "bg-white text-slate-900 border-white",
    tag: "bg-sky-100 text-sky-700",
    check: "text-sky-600 bg-sky-100",
    feat: "text-slate-600",
    btn: "bg-sky-400 text-white hover:bg-sky-500",
    note: "text-slate-400",
  },
};

export const Camps = ({ onNavigate }) => {
  const [pricing, setPricing] = useState({ is_early_bird: true, early_bird_deadline: null });

  useEffect(() => {
    axios.get(`${API}/config/pricing`).then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  const earlyBird = pricing.is_early_bird;
  const deadline = pricing.early_bird_deadline
    ? new Date(pricing.early_bird_deadline).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "1 November 2026";

  return (
    <section
      id="camps"
      className="relative bg-slate-900 py-24 md:py-36 overflow-hidden"
      data-testid="camps-section"
    >
      <div className="pointer-events-none absolute top-10 right-0 h-96 w-96 rounded-full bg-sky-500/20 blur-3xl" />
      <div className="mx-auto max-w-7xl px-5 relative z-10">
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-block font-display font-bold text-sky-400 uppercase tracking-widest text-sm mb-4">
            Camps & Pricing
          </span>
          <h2 className="font-display font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            The Christmas <span className="text-sky-400">Camp.</span>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-slate-300 font-medium">
            Book the full four-day camp for the best value, or come along for
            individual days — whatever suits your family.
          </p>
        </div>

        {/* Early-bird banner */}
        {earlyBird && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="early-bird-banner"
            className="mt-10 mx-auto max-w-2xl rounded-2xl bg-yellow-400 px-6 py-4 flex items-center justify-center gap-3 text-center"
          >
            <Sparkles className="h-5 w-5 text-slate-900 shrink-0" />
            <p className="font-display font-bold text-slate-900">
              Early-bird prices on now — book by {deadline} to save
            </p>
          </motion.div>
        )}

        {/* Camp info strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-8 grid gap-4 md:grid-cols-3"
        >
          {[
            { icon: CalendarDays, label: "Christmas Camp", value: "14–17 December" },
            { icon: Users, label: "Age Range", value: "Years 1 to 8" },
            { icon: MapPin, label: "Location", value: CONTACT.location },
          ].map((item) => (
            <div key={item.label} className="rounded-2xl bg-white/5 border border-white/10 p-6 flex items-center gap-4">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-sky-400 text-slate-900">
                <item.icon className="h-6 w-6" />
              </span>
              <div>
                <p className="text-slate-400 text-sm font-bold uppercase tracking-wide">
                  {item.label}
                </p>
                <p className="text-white font-display font-extrabold text-lg">
                  {item.value}
                </p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Camp times & extended hours */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          data-testid="camp-times"
          className="mt-6 rounded-[2rem] bg-white/5 border border-white/10 p-6 md:p-8"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-sky-400 text-slate-900">
              <Clock className="h-5 w-5" />
            </span>
            <h3 className="font-display font-extrabold text-white text-xl">
              Camp times &amp; extended hours
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-slate-800/60 p-5">
              <p className="font-bold text-white">Drop-off</p>
              <p className="text-slate-300 font-medium mt-1">
                Regular: <span className="text-white font-bold">8.30–9.00am</span>
              </p>
              <p className="text-sky-300 font-semibold mt-1">
                + £10 early drop-off: 8.00–8.30am
              </p>
            </div>
            <div className="rounded-2xl bg-slate-800/60 p-5">
              <p className="font-bold text-white">Collection</p>
              <p className="text-slate-300 font-medium mt-1">
                Regular: <span className="text-white font-bold">4.30–5.00pm</span>
              </p>
              <p className="text-sky-300 font-semibold mt-1">
                + £10 late collection: 5.00–5.30pm
              </p>
            </div>
          </div>
          <p className="mt-4 text-sm font-semibold text-yellow-300 bg-yellow-400/10 rounded-xl px-4 py-3">
            Please note: early drop-off and late collection are separate £10
            add-ons. Booking early drop-off does not include late collection, and
            late collection does not include early drop-off. Add either (or both)
            per child when you book.
          </p>
        </motion.div>

        {/* Pricing cards */}
        <motion.div
          className="mt-8 grid gap-6 md:grid-cols-2 max-w-4xl mx-auto"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ show: { transition: { staggerChildren: 0.12 } } }}
        >
          {PRICING.map((p) => {
            const s = cardStyles[p.variant];
            const activePrice = earlyBird ? p.earlyPrice : p.standardPrice;
            return (
              <motion.div
                key={p.id}
                variants={{
                  hidden: { opacity: 0, y: 40 },
                  show: { opacity: 1, y: 0, transition: { ease: [0.22, 1, 0.36, 1], duration: 0.5 } },
                }}
                whileHover={{ y: -8 }}
                data-testid={`pricing-card-${p.id}`}
                className={`relative rounded-[2rem] border-2 p-8 shadow-[0_20px_50px_rgba(0,0,0,0.25)] ${s.card}`}
              >
                <span className={`inline-block rounded-full px-4 py-1 text-xs font-bold uppercase tracking-wide ${s.tag}`}>
                  {p.tag}
                </span>
                <h3 className="mt-5 font-display font-extrabold text-2xl">{p.name}</h3>
                <div className="mt-3 flex items-end gap-2">
                  <span className="font-display font-black text-5xl leading-none">
                    {activePrice}
                  </span>
                  <span className="font-bold opacity-70 mb-1">{p.unit}</span>
                </div>
                <p className={`mt-2 text-sm font-semibold ${s.note}`} data-testid={`price-note-${p.id}`}>
                  {earlyBird
                    ? `Early-bird price (10% off) · ${p.standardPrice} after ${deadline}`
                    : "Standard price"}
                </p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${s.check}`}>
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      </span>
                      <span className={`font-semibold ${s.feat}`}>{f}</span>
                    </li>
                  ))}
                </ul>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onNavigate("#booking")}
                  data-testid={`pricing-book-${p.id}`}
                  className={`mt-8 w-full rounded-full py-3.5 font-display font-bold transition-colors ${s.btn}`}
                >
                  Book This
                </motion.button>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Coming soon */}
        <div className="mt-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-1.5 text-sm font-bold text-sky-400">
              <Clock className="h-4 w-4" /> More camps coming soon
            </span>
            <p className="mt-4 text-slate-300 font-medium">
              Booking isn&apos;t open yet — but here&apos;s a first look at what&apos;s
              on the way. Keep an eye out, dates will be released soon.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            {UPCOMING_CAMPS.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`upcoming-camp-${c.id}`}
                className="relative rounded-[2rem] border-2 border-dashed border-white/15 bg-white/5 p-8 overflow-hidden"
              >
                <span className="absolute top-6 right-6 rounded-full bg-sky-400/20 border border-sky-400/40 px-3 py-1 text-xs font-bold text-sky-300 uppercase tracking-wide">
                  Coming soon
                </span>
                <p className="font-display font-bold text-sky-400 uppercase tracking-widest text-xs">
                  {c.season}
                </p>
                <h3 className="mt-2 font-display font-extrabold text-3xl text-white">
                  {c.name}
                </h3>
                <p className="mt-3 text-slate-300 font-medium max-w-md">{c.blurb}</p>
                <button
                  disabled
                  data-testid={`upcoming-book-${c.id}`}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 font-display font-bold text-slate-400 cursor-not-allowed"
                >
                  Booking opens soon
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="mt-12 text-center text-slate-400 font-medium">
          Ready to book the Christmas Camp? Build your booking below and pay
          securely online.
        </p>
      </div>
    </section>
  );
};

export default Camps;
